/* Do not change this file. */

import { mapState, mapGetters, mapActions } from "vuex";
import { debounceAsync } from "@/utils";
import config from "@/data/config.json";

/**
 * a singleton debounced version of updateEntry()
 */
let debouncedUpdateEntry = null;

const DatastoreAPI = {
  computed: {
    ...mapState("env", ["publicKey"]),
    ...mapGetters(["getToken", "useAuth", "publicKey", "useDatastore"]),
    url() {
      return (
        "https://courses.ecampus.oregonstate.edu/services/datastore/public" +
        "/v1/entry?public_key=" +
        this.publicKey
      );
    },
    urlEntries() {
      return (
        "https://courses.ecampus.oregonstate.edu/services/datastore/public" +
        "/v1/entries?public_key=" +
        this.publicKey
      );
    },
    config() {
      return {
        headers: { Authorization: `Bearer ${this.getToken}` }
      };
    }
  },
  methods: {
    ...mapActions({
      setEntry: "datastore/setEntry",
      setEntries: "datastore/setEntries"
    }),
    validate() {
      if (!this.useAuth) {
        return false;
      }

      if (!this.useDatastore) {
        return false;
      }

      if (!this.publicKey) {
        console.warn("Attempting call without public_key.");
      }

      if (!this.getToken) {
        console.error("Attempting call without token.");
        return false;
      }

      return true;
    },
    readEntry(cb) {
      if (!this.validate()) {
        return;
      }

      console.log("Attempting Reading Entry...");

      this.axios
        .get(this.url, this.config)
        .then((response) => {
          console.log(
            "Reading Entry Response",
            response.data.message.entry.value
          );
          this.setEntry(response.data.message.entry.value);
        })
        .catch((error) => {
          console.error("Reading Error", error);
        })
        .finally(() => {
          console.log("Completing Reading Entry...");

          if (typeof cb === "function") {
            cb();
          }
        });
    },
    deleteEntry(cb) {
      if (!this.validate()) {
        return;
      }

      console.log("Attempting Reading Entry...");

      this.axios
        .delete(this.url, this.config)
        .then((response) => {
          console.log(
            "Deleting Entry Response",
            response.data.message.entry.value
          );
        })
        .catch((error) => {
          console.error("Deleting Error", error);
        })
        .finally(() => {
          console.log("Completing Deleting Entry...");

          if (typeof cb === "function") {
            cb();
          }
        });
    },
    deleteEntries(cb) {
      if (!this.validate()) {
        return;
      }

      console.log("Attempting Delete Entries...");

      this.axios
        .delete(this.urlEntries, this.config)
        .then((response) => {
          console.log(
            "Deleting Entries Response",
            response.data.message.entry.value
          );
        })
        .catch((error) => {
          console.error("Deleting Error", error);
        })
        .finally(() => {
          console.log("Completing Deleting Entries...");

          if (typeof cb === "function") {
            cb();
          }
        });
    },
    readEntries(cb) {
      if (!this.validate()) {
        return;
      }

      console.log("Attempting Reading Entries...");

      this.axios
        .get(this.urlEntries, this.config)
        .then((response) => {
          console.log("Reading Entries Response", response.data.message);
          this.setEntries(response.data.message.entry);
        })
        .catch((error) => {
          console.error("Reading Error", error);
        })
        .finally(() => {
          console.log("Completing Reading Entries...");

          if (typeof cb === "function") {
            cb();
          }
        });
    },
    /**
     *
     * @param {object} data
     * @param {function} cb
     * @returns {Promise<object>}
     * @throws {Error} if data is empty or invalid
     */
    updateEntry(data, cb) {
      if (!debouncedUpdateEntry) {
        debouncedUpdateEntry = debounceAsync(
          (data, cb) => {
            if (!this.validate()) {
              throw new Error("Invalid request");
            }

            console.log("Attempting Updating Entry...");

            const payload = {
              value: data
            };

            if (!data) {
              throw new Error("updateEntry called with empty payload");
            }

            return this.axios
              .put(this.url, payload, this.config)
              .then((response) => {
                console.log("Update Entry Response", response);
                this.setEntry(response.data.message.entry.value);
              })
              .catch((error) => {
                console.error("Update Error", error);
              })
              .finally(() => {
                console.log("Completing Updating Entry...");

                if (typeof cb === "function") {
                  cb();
                }
              });
          },
          config.settings.datastore.debounceTime || 1000,
          "updateEntry"
        );
      }
      return debouncedUpdateEntry(data, cb);
    }
  }
};

export default DatastoreAPI;
